import { targetDate } from '../dispatchEta.mjs';
// Record Delivery (mock A3) — pure helpers. Caps are the API's (record_delivery_event / DISP-3); the form only pre-checks for a fast message.
//   slip confirm: EVERY slip line is sent with its dispatch_id, each 0 < qty <= dispatched (lower = shortfall back to the order);
//   free entry  : per line 0 < qty <= qty_remaining - qty_open_dispatched (what no open slip reserves). The two are never mixed in one call.
import { lineCap, isLive, qtyProblem } from '../dispatchModel.mjs';
import { convertQty, parseFixed } from '../../PriceList/priceConversion.mjs';   // the web twin of the API's lib/saleUnit (exact BigInt; the API converts again and stays the authority)

const num = v => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const r3 = x => Math.round(x * 1000) / 1000;

export { lineCap };
// Lines that can still take a delivery: something remaining, not cancelled / quote / delivered.
export const openLines = lines => (lines || []).filter(l => num(l.qty_remaining) > 0 && isLive(l));

// 47080 (R5b): a direct-delivery line on a line sold in a sale unit is typed in the SALE unit by default (units[sale_id] = 'sale' | 'base');
// it is sent as { quantity: <sale text>, entry_unit: 'sale' } and the API converts with the line's frozen ratio. Unstamped lines stay base.
// The line's frozen sale unit for ENTRY: any positive ratio except exactly 1 (the API converts every positive frozen ratio; the display helper
// dispatchUnits.saleUnit only shows ratios > 1). Exact text, no Number(). -> { label, ratio } | null
const ONE = parseFixed('1');
export const entryStamp = l => { const r = parseFixed(l.sale_unit_ratio); return l.sale_unit_label && r != null && r > 0n && r !== ONE ? { label: String(l.sale_unit_label), ratio: String(l.sale_unit_ratio) } : null; };
export const unitOf = (l, units = {}) => (entryStamp(l) ? units[l.sale_id] || 'sale' : 'base');
// the typed text in `unit` -> { base, sale, saleExact, wholeSale, baseOk } (exact) | null (no sale unit / not a number). dp = the item's decimals (null = 2).
export const freeConv = (l, raw, unit, dp) => (entryStamp(l) && raw != null && raw !== '' ? convertQty({ typed: String(raw), unit, ratio: String(l.sale_unit_ratio), baseDp: dp }) : null);

// Free entry -> { lines, bases } | { problem: { sale_id, kind: 'positive' | 'exceeds' | 'grid' | 'none' } }. Empty inputs are skipped.
// bases[sale_id] = the BASE quantity (what the line's caps and the recorded summary count). dpOf(line) = the item's decimals.
export function buildFree(lines, qtys, units = {}, dpOf = () => null) {
  const out = [], bases = {};
  for (const l of openLines(lines)) {
    const raw = qtys[l.sale_id];
    if (raw == null || raw === '') continue;
    const sale = unitOf(l, units) === 'sale', c = sale ? freeConv(l, raw, 'sale', dpOf(l)) : null;
    const kind = sale && !c ? 'positive' : sale && !c.baseOk ? 'grid' : qtyProblem(sale ? c.base : raw, lineCap(l));
    if (kind) return { lines: [], bases: {}, problem: { sale_id: l.sale_id, kind } };
    out.push(sale ? { sale_id: l.sale_id, quantity: c.sale, entry_unit: 'sale' } : { sale_id: l.sale_id, quantity: Number(raw) });
    bases[l.sale_id] = sale ? Number(c.base) : Number(raw);   // display only (summary rows); the API counts the exact text
  }
  return out.length ? { lines: out, bases, problem: null } : { lines: [], bases: {}, problem: { kind: 'none' } };
}

// Slip confirm: per slip line 'positive' | 'exceeds' | null for the typed quantity.
// Owner ruling: on a MULTI-line slip a line may be delivered at 0 (that item is genuinely missing: it stays open on the order);
// a ONE-line slip must stay positive. At least one line > 0 is checked by slipAllZero.
export function slipLineIssue(sl, raw, multi = false) {
  const q = Number(raw);
  if (multi && raw != null && raw !== '' && Number.isFinite(q) && q === 0) return null;
  return qtyProblem(raw, sl.quantity);
}
export const isZeroLine = (raw, multi) => multi && raw != null && raw !== '' && Number(raw) === 0;
// every line typed as 0 (no line delivered): the API refuses it too (DISPATCH_ALL_ZERO) — point the user to Cancel dispatch instead.
export const slipAllZero = (slip, qtys) => slip.lines.length > 1 && slip.lines.every(sl => isZeroLine(qtys[sl.id], true));
// -> { lines } | { problem: { id, kind } | { kind: 'allZero' } }. EVERY slip line is sent (0 included), each with its dispatch_id.
export function buildSlip(slip, qtys) {
  const multi = slip.lines.length > 1;
  const lines = [];
  for (const sl of slip.lines) {
    const kind = slipLineIssue(sl, qtys[sl.id], multi);
    if (kind) return { lines: [], problem: { id: sl.id, kind } };
    lines.push({ sale_id: sl.sales_id, quantity: Number(qtys[sl.id]), dispatch_id: sl.id });
  }
  return slipAllZero(slip, qtys) ? { lines: [], problem: { kind: 'allZero' } } : { lines, problem: null };
}
// Quantity not delivered on this slip line (goes back to the order), never negative.
export const slipLineShort = (sl, raw) => { const q = Number(raw); return r3(Math.max(0, num(sl.quantity) - (Number.isFinite(q) ? q : 0))); };

// "Already delivered". With the order's delivery events (GES-431 D10, GET /api/sales-orders/:op/delivery-events): one row per
// delivery event (its delivery date, the BL it confirmed or 'no slip', one entry per delivered line). Without them (read not loaded /
// failed / older API): derived from the order payload as before: one row per delivered slip (its lines' delivered_qty, dated at close),
// plus one 'no slip' row per line for what was delivered outside any slip.
// -> [{ key, date, bl, entries: [{ qty, line }] }]
export function priorDeliveries(lines, dispatches, events = null) {
  const byId = new Map((lines || []).map(l => [l.sale_id, l]));
  if (Array.isArray(events)) {
    const rows = new Map();
    for (const e of events) {
      if (!byId.has(e.sales_id) || !(num(e.quantity_delivered) > 0)) continue;
      if (!rows.has(e.event)) rows.set(e.event, { key: e.event, date: e.delivery_date, bl: e.dispatch_no || null, entries: [] });
      rows.get(e.event).entries.push({ qty: num(e.quantity_delivered), line: byId.get(e.sales_id) });
    }
    return [...rows.values()].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  }
  const viaSlip = new Map();
  const rows = [];
  for (const d of (dispatches || []).filter(x => x.status === 'delivered')) {
    const entries = (d.lines || []).filter(sl => num(sl.delivered_qty) > 0 && byId.has(sl.sales_id)).map(sl => {
      viaSlip.set(sl.sales_id, num(viaSlip.get(sl.sales_id)) + num(sl.delivered_qty));
      return { qty: num(sl.delivered_qty), line: byId.get(sl.sales_id) };
    });
    if (entries.length) rows.push({ key: d.slip_id, date: d.closed_at || d.dispatch_date, bl: d.dispatch_no, entries });
  }
  for (const l of lines || []) {
    const rest = r3(num(l.qty_delivered) - num(viaSlip.get(l.sale_id)));
    if (rest > 0) rows.push({ key: 'free-' + l.sale_id, date: l.last_delivery_date || null, bl: null, entries: [{ qty: rest, line: l }] });
  }
  return rows.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
}

// ── Soft date notes (never blocking: the owner's rule is to never refuse because of dates). All on BUSINESS dates (YYYY-MM-DD strings,
// local today), never UTC timestamps, same day arithmetic as the dispatch lateness helper (dispatchesModel.ageDays).
const DAY = 86400000;
const dayNum = iso => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '')); return m ? Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3]) / DAY) : null; };
export const daysAfter = (later, earlier) => { const a = dayNum(later), b = dayNum(earlier); return a == null || b == null ? null : a - b; };
// -> [{ kind: 'before' | 'future' | 'afterEta' | 'afterLine', tone: 'warn' | 'info', date?, count?, item? }]
//   (before-dispatch is now a blocking error, see deliveryDateProblem)
//   before    : delivery date earlier than the slip's dispatch date (it is the recognition date: revenue + stock land in that earlier period)
//   future    : delivery date after today (both steps)
//   afterEta  : slip step, N days after the slip's own ETA
//   afterLine : direct step, N days after a line's expected delivery date
// DELIVERY date window (owner): dispatches may be future-dated (scheduling) but a DELIVERY never is, and a slip delivery cannot precede its dispatch date:
// valid = [dispatch date, today]; a future-dated slip has an EMPTY window (cannot be delivered yet). Free entry has no dispatch date: [-, today].
export const deliveryWindow = (slip, today) => { const min = slip?.dispatch_date || null; return { min, max: today, empty: !!min && min > today }; };
// 'before' | 'future' | 'scheduled' (window empty) | null  (the API refuses DISPATCH_DELIVERY_BEFORE_DISPATCH / DELIVERY_DATE_IN_FUTURE too)
export function deliveryDateProblem(date, slip, today) {
  const w = deliveryWindow(slip, today);
  if (w.empty) return 'scheduled';
  if (!date) return null;
  return w.min && date < w.min ? 'before' : date > w.max ? 'future' : null;
}
export function dateNotes({ date, today, slip, lines }) {
  if (!date) return [];
  const out = [];
  if (slip) {
    const target = targetDate(slip), n = target ? daysAfter(date, target) : null;   // the dispatch ETA (target date; legacy null ETA = dispatch date)
    if (n > 0) out.push({ kind: 'afterEta', tone: 'info', count: n, date: target });
  } else {
    for (const l of lines || []) { const n = l.expected_delivery_date ? daysAfter(date, l.expected_delivery_date) : null; if (n > 0) out.push({ kind: 'afterLine', tone: 'info', count: n, date: l.expected_delivery_date, item: l.item_name }); }
  }
  return out;
}
